/**
 * Generic report table.
 *
 * Same look and behaviour as the Conversation Detail table — sticky header,
 * pinned identity column, fixed widths — but the cell formatting is driven by
 * the column's declared `type` rather than by hardcoded key lists, so a new
 * report gets correct dates, durations and percentages by declaring them in
 * its catalog instead of editing the table.
 */
import { useEffect, useRef, useState, useMemo, type JSX } from 'react'
import { Play, Square, Loader2, Download, AlertCircle, FileText } from 'lucide-react'

import type { ReportColumnDef } from '../agent-performance/columns.js'
import { formatSeconds } from '../analytics/viz.js'
import { ResizeHandle, useColumnResize } from '../../../hooks/useColumnResize.js'

export interface ReportDataTableProps {
  rows: readonly Record<string, unknown>[]
  columns: ReportColumnDef[]
  visibleColumns: string[]
  onRowClick?: (row: Record<string, unknown>) => void
}

function Blank(): JSX.Element {
  return <span className="text-slate-300">—</span>
}

/** ISO timestamp → `14 Aug 2026 05:44`, split so the date reads as the anchor. */
function formatDateTime(value: unknown): { date: string; time: string } | null {
  if (typeof value !== 'string' && !(value instanceof Date)) return null
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return {
    date: date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }),
    time: date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
  }
}


/**
 * Inline player for a call recording.
 *
 * Streams `GET /recordings/:id/stream`, which is gated on RECORDINGS_VIEW —
 * separately from the CALLS_VIEW that lets someone read this report. An
 * operator who can see the column but not hear the audio gets a 403 here, which
 * is the intended behaviour: reading a CDR and listening to a customer's voice
 * are different acts.
 *
 * Fetched as a blob rather than pointed at with `<audio src>` so a 403 surfaces
 * as a visible state instead of a silent, broken player.
 */
function RecordingPlayer({ recordingId, seconds }: {
  recordingId: string
  seconds: number | null
}): JSX.Element {
  const [state, setState] = useState<'idle' | 'loading' | 'playing' | 'denied' | 'error'>('idle')
  const [downloading, setDownloading] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const urlRef = useRef<string | null>(null)

  useEffect(() => () => {
    audioRef.current?.pause()
    if (urlRef.current) URL.revokeObjectURL(urlRef.current)
  }, [])

  async function toggle(): Promise<void> {
    if (state === 'playing') {
      audioRef.current?.pause()
      setState('idle')
      return
    }
    if (urlRef.current && audioRef.current) {
      await audioRef.current.play()
      setState('playing')
      return
    }
    setState('loading')
    try {
      const res = await fetch(`/api/recordings/${recordingId}/stream`, { credentials: 'include' })
      if (res.status === 403) { setState('denied'); return }
      if (!res.ok) throw new Error(String(res.status))
      const blob = await res.blob()
      urlRef.current = URL.createObjectURL(blob)
      const audio = new Audio(urlRef.current)
      audioRef.current = audio
      audio.onended = () => setState('idle')
      audio.onerror = () => setState('error')
      await audio.play()
      setState('playing')
    } catch {
      // Was silently reset to idle — a broken recording looked like a
      // button that did nothing. Say so.
      setState('error')
    }
  }

  async function download(): Promise<void> {
    setDownloading(true)
    try {
      const res = await fetch(`/api/recordings/${recordingId}/download`, { credentials: 'include' })
      if (!res.ok) throw new Error(String(res.status))
      const url = URL.createObjectURL(await res.blob())
      const a = document.createElement('a')
      a.href = url
      a.download = `recording-${recordingId}.wav`
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
    } catch {
      setState('error')
    } finally {
      setDownloading(false)
    }
  }

  if (state === 'denied') {
    return <span className="text-[11px] text-slate-400" title="Requires the Recordings View permission">no access</span>
  }

  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); void toggle() }}
        title={state === 'error' ? 'Recording could not be played — click to retry'
          : seconds ? `Play recording (${formatSeconds(seconds)})` : 'Play recording'}
        className={`inline-flex items-center gap-1 rounded-[3px] px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset transition-colors ${
          state === 'error'
            ? 'bg-red-50 text-red-700 ring-red-200 hover:bg-red-100'
            : state === 'playing'
              ? 'bg-blue-600 text-white ring-blue-600 hover:bg-blue-700'
              : 'bg-blue-50 text-blue-700 ring-blue-200 hover:bg-blue-100'
        }`}
      >
        {state === 'loading' ? <Loader2 className="h-3 w-3 animate-spin" />
          : state === 'playing' ? <Square className="h-3 w-3" />
            : state === 'error' ? <AlertCircle className="h-3 w-3" />
              : <Play className="h-3 w-3" />}
        {state === 'playing' ? 'Stop' : state === 'error' ? 'Retry' : 'Play'}
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); void download() }}
        title="Download recording"
        aria-label="Download recording"
        className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-[3px] text-slate-500 ring-1 ring-inset ring-slate-200 transition-colors hover:bg-slate-50 hover:text-slate-800"
      >
        {downloading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
      </button>
    </span>
  )
}

const TRANSCRIPT_BADGE: Record<string, { label: string; className: string }> = {
  COMPLETED: { label: 'Available', className: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  IN_PROGRESS: { label: 'Processing', className: 'bg-amber-50 text-amber-700 ring-amber-200' },
  QUEUED: { label: 'Queued', className: 'bg-amber-50 text-amber-700 ring-amber-200' },
  RETRY: { label: 'Needs attention', className: 'bg-amber-50 text-amber-700 ring-amber-200' },
  FAILED: { label: 'Failed', className: 'bg-red-50 text-red-700 ring-red-200' },
}

// ── Live transcript status ─────────────────────────────────────────────────
//
// One shared poller for every badge on screen: rows whose transcript is not
// yet terminal are batched into a single request every few seconds, and the
// poller stops itself once nothing on screen is pending.

const TERMINAL_TRANSCRIPT = new Set(['COMPLETED', 'FAILED'])
const POLL_MS = 6_000
const transcriptListeners = new Map<string, Set<(status: string | null) => void>>()
let transcriptTimer: ReturnType<typeof setTimeout> | null = null

function scheduleTranscriptPoll(): void {
  if (transcriptTimer !== null || transcriptListeners.size === 0) return
  transcriptTimer = setTimeout(() => {
    transcriptTimer = null
    const ids = [...transcriptListeners.keys()]
    if (ids.length === 0) return
    void fetch(`/api/reports/calls/history-transcripts?ids=${ids.join(',')}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() as Promise<{ statuses: Record<string, string | null> }> : null))
      .then((body) => {
        if (body) {
          for (const [id, status] of Object.entries(body.statuses)) {
            transcriptListeners.get(id)?.forEach((fn) => fn(status))
          }
        }
      })
      .catch(() => { /* next tick retries */ })
      .finally(scheduleTranscriptPoll)
  }, POLL_MS)
}

/** A row with no status may simply belong to a tenant without post-call analytics. */
const MAX_UNSTARTED_POLLS = 30

function useLiveTranscriptStatus(recordingId: string | null, initial: string | null): string | null {
  const [status, setStatus] = useState(initial)
  const unstartedPolls = useRef(0)
  useEffect(() => { setStatus(initial); unstartedPolls.current = 0 }, [initial])
  useEffect(() => {
    if (!recordingId || (status !== null && TERMINAL_TRANSCRIPT.has(status))) return
    if (status === null && unstartedPolls.current >= MAX_UNSTARTED_POLLS) return
    const set = transcriptListeners.get(recordingId) ?? new Set()
    const listener = (next: string | null): void => {
      // Status stays null ⇒ the effect never re-runs, so stop polling here.
      if (next === null && ++unstartedPolls.current >= MAX_UNSTARTED_POLLS) {
        set.delete(listener)
        if (set.size === 0) transcriptListeners.delete(recordingId)
      }
      setStatus(next)
    }
    set.add(listener)
    transcriptListeners.set(recordingId, set)
    scheduleTranscriptPoll()
    return () => {
      set.delete(listener)
      if (set.size === 0) transcriptListeners.delete(recordingId)
    }
  }, [recordingId, status])
  return status
}

function LiveTranscriptBadge({ recordingId, initial }: { recordingId: string | null; initial: string | null }): JSX.Element {
  const status = useLiveTranscriptStatus(recordingId, initial)
  const badge = status ? TRANSCRIPT_BADGE[status] : undefined
  if (!badge) return <span className="text-[11px] text-slate-400">—</span>
  return (
    <span className={`inline-flex items-center gap-1 rounded-[3px] px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${badge.className}`}>
      {status === 'COMPLETED' && <FileText className="h-3 w-3" />}
      {(status === 'QUEUED' || status === 'IN_PROGRESS') && <Loader2 className="h-3 w-3 animate-spin" />}
      {status === 'FAILED' && <AlertCircle className="h-3 w-3" />}
      {badge.label}
    </span>
  )
}

function cellFor(row: Record<string, unknown>, col: ReportColumnDef): JSX.Element {
  const value = row[col.key]

  // Checked before the blank guard below: `hasRecording === false` is a real
  // answer ("this call has no audio"), not a missing value.
  if (col.type === 'recording') {
    const id = row['recordingId']
    if (value !== true || typeof id !== 'string') {
      return <span className="text-[11px] text-slate-400">—</span>
    }
    const secs = row['recordingSec']
    return <RecordingPlayer recordingId={id} seconds={typeof secs === 'number' ? secs : null} />
  }

  if (col.type === 'transcript') {
    const id = row['recordingId']
    return (
      <LiveTranscriptBadge
        recordingId={typeof id === 'string' ? id : null}
        initial={typeof value === 'string' ? value : null}
      />
    )
  }

  if (value === null || value === undefined || value === '') return <Blank />

  switch (col.type) {
    case 'datetime': {
      const parts = formatDateTime(value)
      if (!parts) return <Blank />
      return (
        <span className="tabular-nums whitespace-nowrap">
          <span className="text-slate-700">{parts.date}</span>
          <span className="ml-1.5 text-slate-400">{parts.time}</span>
        </span>
      )
    }
    case 'duration': {
      const seconds = typeof value === 'number' ? value : Number(value)
      if (!Number.isFinite(seconds)) return <Blank />
      return <span className="tabular-nums">{formatSeconds(seconds)}</span>
    }
    case 'percent': {
      const pct = typeof value === 'number' ? value : Number(value)
      if (!Number.isFinite(pct)) return <Blank />
      return <span className="tabular-nums">{pct.toFixed(1)}%</span>
    }
    case 'number': {
      const n = typeof value === 'number' ? value : Number(value)
      if (!Number.isFinite(n)) return <Blank />
      return <span className="tabular-nums">{n.toLocaleString()}</span>
    }
    case 'mono':
      return (
        <span className="truncate font-mono text-[12px] font-semibold text-slate-900">
          {String(value)}
        </span>
      )
    case 'badge': {
      const text = String(value)
      return (
        <span className="inline-flex items-center rounded-[3px] bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-slate-600">
          {text}
        </span>
      )
    }
    case 'boolean':
      return (
        <span
          className={`inline-flex items-center rounded-[3px] px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${
            value === true
              ? 'bg-amber-50 text-amber-700 ring-amber-200'
              : 'bg-slate-50 text-slate-500 ring-slate-200'
          }`}
        >
          {value === true ? 'Yes' : 'No'}
        </span>
      )
    default: {
      const text = String(value).trim()
      if (!text) return <Blank />
      return <span className="truncate" title={text}>{text}</span>
    }
  }
}

export default function ReportDataTable({
  rows,
  columns,
  visibleColumns,
  onRowClick,
}: ReportDataTableProps): JSX.Element {
  const byKey = new Map(columns.map((c) => [c.key, c]))

  // Locked columns first (their stickyLeft offsets assume it), then the
  // user's order.
  const ordered: ReportColumnDef[] = [
    ...columns.filter((c) => c.locked && visibleColumns.includes(c.key)),
    ...visibleColumns
      .map((k) => byKey.get(k))
      .filter((c): c is ReportColumnDef => c !== undefined && !c.locked),
  ]

  // Resizable column widths — initialised from the catalog, reset when the
  // column set changes.
  const initialWidths = useMemo(() => ordered.map((c) => c.width), [ordered])
  const { columnWidths, handleResize } = useColumnResize(initialWidths)

  let stickyAcc = 0
  const laidOut = ordered.map((c, i) => {
    const width = columnWidths[i] ?? c.width
    if (c.stickyLeft === undefined) return { ...c, width }
    const stickyLeft = stickyAcc
    stickyAcc += width
    return { ...c, width, stickyLeft }
  })
  const lastStickyKey = [...laidOut].reverse().find((c) => c.stickyLeft !== undefined)?.key

  return (
    <table
      className="w-full min-w-max border-separate border-spacing-0 text-[13px]"
      style={{ tableLayout: 'fixed' }}
    >
      <thead>
        <tr>
          {laidOut.map((col, i) => (
            <th
              key={col.key}
              style={{
                width: col.width,
                minWidth: col.width,
                ...(col.stickyLeft !== undefined ? { left: col.stickyLeft } : {}),
              }}
              className={`relative sticky top-0 h-11 border-b border-slate-200 bg-[#FAFBFC] px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500 ${
                col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
              } ${col.stickyLeft !== undefined ? 'z-30' : 'z-20'} ${
                col.key === lastStickyKey ? 'shadow-[1px_0_0_0_rgb(226,232,240)]' : ''
              }`}
            >
              <span className="truncate">{col.label}</span>
              <ResizeHandle
                startWidth={col.width}
                onResize={(w) => handleResize(i, w)}
                onResizeEnd={(w) => handleResize(i, w)}
              />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr
            key={String(row['id'] ?? row['groupKey'] ?? i)}
            className={`group ${onRowClick ? 'cursor-pointer' : ''}`}
            onClick={() => onRowClick?.(row)}
          >
            {laidOut.map((col) => (
              <td
                key={col.key}
                style={{
                  width: col.width,
                  minWidth: col.width,
                  maxWidth: col.width,
                  ...(col.stickyLeft !== undefined ? { left: col.stickyLeft } : {}),
                }}
                className={`h-[54px] border-b border-slate-100 bg-white px-4 align-middle text-slate-700 transition-colors group-hover:bg-[#F7F9FC] ${
                  col.stickyLeft !== undefined ? 'sticky z-10' : ''
                } ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''} ${
                  col.key === lastStickyKey ? 'shadow-[1px_0_0_0_rgb(241,245,249)]' : ''
                }`}
              >
                <div
                  className={
                    col.align === 'center'
                      ? 'flex items-center justify-center'
                      : col.align === 'right'
                        ? 'flex items-center justify-end'
                        : 'truncate'
                  }
                >
                  {cellFor(row, col)}
                </div>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
